/**
 * CAS (Component Award Scheme) data loader and lookup utilities.
 * Loads cas_awards_lean.json lazily; subsequent calls return the cached promise.
 * @module utils/cas-data
 */
/// <reference types="vite/client" />

import type {
  CasAwardRuleGroup,
  CasAwardRuleItem,
  CasAwardsData,
  CasComponent,
  CasMajorAward,
} from "@/types/cas";

// Cached promise — data loads once for the lifetime of the page
let dataPromise: Promise<CasAwardsData> | null = null;

/**
 * Load the CAS awards data from public/assets. Returns a shared promise so the
 * fetch is performed at most once regardless of how many components call this.
 */
export function loadCasData(): Promise<CasAwardsData> {
  if (!dataPromise) {
    const baseUrl = import.meta.env.BASE_URL ?? "/";
    const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
    const casDataUrl = `${normalizedBaseUrl}assets/cas_awards_lean.json`;
    const pending = fetch(casDataUrl).then((r) => {
      if (!r.ok) {
        throw new Error(`Failed to load CAS data: ${r.status} ${r.statusText}`);
      }
      return r.json() as Promise<CasAwardsData>;
    });
    // A failed read is not kept, so the next caller asks again.
    pending.catch(() => {
      if (dataPromise === pending) {
        dataPromise = null;
      }
    });
    dataPromise = pending;
  }
  return dataPromise;
}

/** Reset the data cache (used in tests). */
export function resetCasDataCache(): void {
  dataPromise = null;
}

/**
 * Build a code→CasMajorAward lookup map.
 */
export function indexMajorAwards(data: CasAwardsData): Map<string, CasMajorAward> {
  return new Map(data.major_awards.map((a) => [a.code, a]));
}

/**
 * Build a code→CasComponent lookup map.
 */
export function indexComponents(data: CasAwardsData): Map<string, CasComponent> {
  return new Map(data.components.map((c) => [c.code, c]));
}

/**
 * Return the rule groups for a specific major award code.
 */
export function getRuleGroupsForAward(data: CasAwardsData, majorCode: string): CasAwardRuleGroup[] {
  return data.award_rule_groups.filter((g) => g.major_award_code === majorCode);
}

/**
 * Return the rule items for a specific major award code.
 */
export function getRuleItemsForAward(data: CasAwardsData, majorCode: string): CasAwardRuleItem[] {
  return data.award_rule_items.filter((i) => i.major_award_code === majorCode);
}

/**
 * A rule group's items. `group.id` is the join when the dataset carries one:
 * two groups may share a label.
 */
export function casGroupItems(
  group: CasAwardRuleGroup,
  items: readonly CasAwardRuleItem[],
): CasAwardRuleItem[] {
  return items.filter((i) =>
    group.id ? i.group_id === group.id : i.group_label === group.group_label,
  );
}

/** Where one rule group stands against the components a programme has added. */
export interface CasGroupStatus {
  items: CasAwardRuleItem[];
  /** Items whose component is added. */
  addedCount: number;
  /** Distinct component codes the group lists. */
  codes: string[];
  /** Credits of the distinct listed components that are added. */
  selectedCredits: number;
  minCredits: number;
  /** Required components in the group that are not added. */
  missingMandatory: string[];
  /** Nothing required is missing and the credit floor is met. */
  satisfied: boolean;
}

export function casGroupStatus(
  group: CasAwardRuleGroup,
  items: readonly CasAwardRuleItem[],
  addedCodes: ReadonlySet<string>,
  creditsOf: (code: string) => number,
): CasGroupStatus {
  const groupItems = casGroupItems(group, items);
  const codes = Array.from(
    new Set(
      groupItems
        .map((i) => i.component_code)
        .filter((code): code is string => typeof code === "string" && code.length > 0),
    ),
  );
  const selectedCredits = codes
    .filter((code) => addedCodes.has(code))
    .reduce((sum, code) => sum + creditsOf(code), 0);
  const minCredits = Number(group.min_credits ?? 0);
  const missingMandatory = Array.from(
    new Set(
      groupItems
        .filter((i) => i.is_mandatory && i.component_code && !addedCodes.has(i.component_code))
        .map((i) => i.component_code as string),
    ),
  );
  return {
    items: groupItems,
    addedCount: groupItems.filter((i) => addedCodes.has(i.component_code ?? "")).length,
    codes,
    selectedCredits,
    minCredits,
    missingMandatory,
    satisfied:
      missingMandatory.length === 0 &&
      (codes.length === 0 || minCredits <= 0 || selectedCredits >= minCredits),
  };
}

/**
 * Extract the top-level field of learning category from the hierarchical string.
 * e.g. "07. Engineering, manufacturing and construction > 9. ..."
 *   → "07. Engineering, manufacturing and construction"
 */
export function getTopLevelField(fieldOfLearning: string): string {
  return fieldOfLearning.split(" > ")[0] ?? fieldOfLearning;
}

/**
 * Return a sorted, deduplicated list of top-level field-of-learning categories
 * from the components array.
 */
export function getTopLevelFields(data: CasAwardsData): string[] {
  const fields = new Set(data.components.map((c) => getTopLevelField(c.field_of_learning)));
  return Array.from(fields).sort();
}

/**
 * Return a sorted, deduplicated list of NFQ levels present in the components array.
 */
export function getComponentNfqLevels(data: CasAwardsData): string[] {
  const levels = new Set(data.components.map((c) => c.nfq_level));
  return Array.from(levels).sort();
}

/**
 * Return a sorted, deduplicated list of award classes in the components array.
 */
export function getComponentAwardClasses(data: CasAwardsData): string[] {
  const classes = new Set(data.components.map((c) => c.award_class));
  return Array.from(classes).sort();
}

/** Filter components by search keyword, NFQ levels, award classes, and top-level field. */
export function filterComponents(
  components: CasComponent[],
  opts: {
    keyword?: string;
    nfqLevels?: string[];
    awardClasses?: string[];
    topLevelField?: string;
  },
): CasComponent[] {
  const kw = (opts.keyword ?? "").toLowerCase().trim();
  const levels = opts.nfqLevels ?? [];
  const classes = opts.awardClasses ?? [];
  const field = opts.topLevelField ?? "";

  return components.filter((c) => {
    if (kw && !c.code.toLowerCase().includes(kw) && !c.title.toLowerCase().includes(kw)) {
      return false;
    }
    if (levels.length > 0 && !levels.includes(c.nfq_level)) {
      return false;
    }
    if (classes.length > 0 && !classes.includes(c.award_class)) {
      return false;
    }
    if (field && getTopLevelField(c.field_of_learning) !== field) {
      return false;
    }
    return true;
  });
}

/** Filter major awards by keyword and NFQ level. */
export function filterMajorAwards(
  awards: CasMajorAward[],
  opts: { keyword?: string; nfqLevel?: string },
): CasMajorAward[] {
  const kw = (opts.keyword ?? "").toLowerCase().trim();
  const level = opts.nfqLevel ?? "";

  return awards.filter((a) => {
    if (kw && !a.code.toLowerCase().includes(kw) && !a.title.toLowerCase().includes(kw)) {
      return false;
    }
    if (level && a.nfq_level !== level) {
      return false;
    }
    return true;
  });
}
